// src/achats/NouvelleCommande.js
import React, { useState, useEffect, useRef } from "react";
import {
    Card, Row, Col, Form, Button, Spinner,
    InputGroup, Table, Badge, Modal
} from "react-bootstrap";
import { useNavigate, useLocation } from "react-router-dom";
import ReactDOM from "react-dom";
import Footer from "../layouts/Footer";
import Header from "../layouts/Header";
import { formatCurrency } from "../utils/formatters";
import { API_BASE_URL } from "../constants/api";
import { COLORS } from "../constants/colors";

/* ============================================================
   TOAST NOTIFICATION — Design charte
   ============================================================ */
const ToastNotification = ({ show, onClose, type, title, message, autoHideDelay = 5000 }) => {
    if (!show) return null;
    const config = {
        success: { bgColor: COLORS.success || '#10b981', icon: 'ri-checkbox-circle-fill' },
        error:   { bgColor: COLORS.danger  || '#ef4444', icon: 'ri-error-warning-fill' },
        info:    { bgColor: COLORS.info    || '#3b82f6', icon: 'ri-information-fill' },
        warning: { bgColor: COLORS.warning || '#f59e0b', icon: 'ri-alert-fill' }
    }[type] || { bgColor: COLORS.info || '#3b82f6', icon: 'ri-information-fill' };

    return (
        <div className="toast-notification-fixed">
            <div className="toast-modern">
                <div className="toast-header-custom" style={{ background: config.bgColor }}>
                    <div className="d-flex align-items-center gap-2">
                        <i className={`${config.icon} text-white`} style={{ fontSize: '1.5rem' }}></i>
                        <strong className="text-white fs-6">{title}</strong>
                    </div>
                    <button onClick={onClose} className="toast-close-btn">
                        <i className="ri-close-line fs-5"></i>
                    </button>
                </div>
                <div className="toast-body-custom"><p className="mb-0">{message}</p></div>
                <div className="toast-progress" style={{ background: config.bgColor, animation: `shrink ${autoHideDelay}ms linear forwards` }}></div>
            </div>
        </div>
    );
};

/* ============================================================
   COMPOSANT PRINCIPAL
   ============================================================ */
export default function NouvelleCommande() {
    const navigate = useNavigate();
    const location = useLocation();
    const [loading, setLoading] = useState(false);
    const [fournisseurs, setFournisseurs] = useState([]);
    const [produits, setProduits] = useState([]);
    const [toast, setToast] = useState({ show: false, type: '', title: '', message: '' });

    // Recherche fournisseur
    const [searchFournisseur, setSearchFournisseur] = useState('');
    const [showFournisseurResults, setShowFournisseurResults] = useState(false);
    const fournisseurInputRef = useRef(null);
    const [fournisseurPosition, setFournisseurPosition] = useState({ top: 0, left: 0, width: 0 });

    // Recherche produit
    const [searchProduit, setSearchProduit] = useState('');
    const [showProduitResults, setShowProduitResults] = useState(false);
    const produitInputRef = useRef(null);
    const [produitPosition, setProduitPosition] = useState({ top: 0, left: 0, width: 0 });

    const [formData, setFormData] = useState({
        id_fournisseur: '',
        fournisseur_nom: '',
        date_commande: new Date().toISOString().split('T')[0],
        date_livraison_prevue: '',
        conditions_reglement: '',
        lignes: []
    });

    const [errors, setErrors] = useState({});
    const [showProduitModal, setShowProduitModal] = useState(false);
    const [produitSelectionne, setProduitSelectionne] = useState(null);
    const [quantite, setQuantite] = useState(1);
    const [prix, setPrix] = useState(0);
    const [remise, setRemise] = useState(0);

    const showToast = (type, title, message) => setToast({ show: true, type, title, message });
    const hideToast = () => setToast({ show: false, type: '', title: '', message: '' });

    /* ============================================================
       EFFECTS
       ============================================================ */
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const produitId = params.get('produit');
        const quantiteParam = params.get('quantite');

        if (produitId) {
            sessionStorage.setItem('produit_auto', produitId);
            sessionStorage.setItem('quantite_auto', quantiteParam || '1');
        }
    }, [location.search]);

    useEffect(() => {
        const produitId = sessionStorage.getItem('produit_auto');
        if (produitId && produits.length > 0) {
            const produit = produits.find(p => p.id_produit === parseInt(produitId));
            const quantiteAuto = parseInt(sessionStorage.getItem('quantite_auto')) || 1;

            if (produit) {
                const nouvelleLigne = {
                    id_produit: produit.id_produit,
                    reference: produit.reference,
                    designation: produit.designation,
                    quantite_commandee: quantiteAuto,
                    prix_unitaire_ht: produit.prix_achat_ht || 0,
                    remise: 0,
                    tva_taux: produit.tva_taux || 0.00,
                    css_taux: produit.css_taux || 0.00,
                    montant_ht: quantiteAuto * (produit.prix_achat_ht || 0)
                };
                setFormData(prev => ({ ...prev, lignes: [...prev.lignes, nouvelleLigne] }));
                showToast('info', 'Produit ajouté', `${produit.designation} ajouté automatiquement`);
                sessionStorage.removeItem('produit_auto');
                sessionStorage.removeItem('quantite_auto');
            }
        }
    }, [produits]);

    useEffect(() => {
        if (location.state?.produits) {
            handleAjouterProduits(location.state.produits);
        }
    }, [location.state, produits]);

    const handleAjouterProduits = (produitsData) => {
        if (!produitsData || produitsData.length === 0 || produits.length === 0) return;

        const nouvellesLignes = produitsData.map(p => {
            const produitComplet = produits.find(prod => prod.id_produit === p.id);
            if (!produitComplet) return null;
            return {
                id_produit: produitComplet.id_produit,
                reference: produitComplet.reference,
                designation: produitComplet.designation,
                quantite_commandee: p.quantite || 1,
                prix_unitaire_ht: produitComplet.prix_achat_ht || 0,
                remise: 0,
                tva_taux: produitComplet.tva_taux || 0.00,
                css_taux: produitComplet.css_taux || 0.00,
                montant_ht: (p.quantite || 1) * (produitComplet.prix_achat_ht || 0)
            };
        }).filter(l => l !== null);

        if (nouvellesLignes.length > 0) {
            setFormData(prev => ({ ...prev, lignes: [...prev.lignes, ...nouvellesLignes] }));
        }
    };

    const updateFournisseurPosition = () => {
        if (fournisseurInputRef.current) {
            const rect = fournisseurInputRef.current.getBoundingClientRect();
            setFournisseurPosition({
                top: rect.bottom + window.scrollY,
                left: rect.left + window.scrollX,
                width: rect.width
            });
        }
    };

    const updateProduitPosition = () => {
        if (produitInputRef.current) {
            const rect = produitInputRef.current.getBoundingClientRect();
            setProduitPosition({
                top: rect.bottom + window.scrollY,
                left: rect.left + window.scrollX,
                width: rect.width
            });
        }
    };

    useEffect(() => {
        const handleScroll = () => {
            if (showFournisseurResults) updateFournisseurPosition();
            if (showProduitResults) updateProduitPosition();
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, [showFournisseurResults, showProduitResults]);

    /* ============================================================
       FETCH
       ============================================================ */
    const fetchFournisseurs = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(`${API_BASE_URL}/fournisseurs.php`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const result = await response.json();
            setFournisseurs(result.data || []);
        } catch (err) {
            console.error("Erreur chargement fournisseurs", err);
        }
    };

    const fetchProduits = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(`${API_BASE_URL}/produits.php`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const result = await response.json();
            setProduits(result.data || []);
        } catch (err) {
            console.error("Erreur chargement produits", err);
        }
    };

    useEffect(() => {
        fetchFournisseurs();
        fetchProduits();
    }, []);

    /* ============================================================
       ACTIONS
       ============================================================ */
    const handleFournisseurSelect = (fournisseur) => {
        setFormData({
            ...formData,
            id_fournisseur: fournisseur.id_fournisseur,
            fournisseur_nom: fournisseur.raison_sociale,
            conditions_reglement: fournisseur.conditions_reglement || ''
        });
        setSearchFournisseur(fournisseur.raison_sociale);
        setShowFournisseurResults(false);
        if (errors.fournisseur) setErrors({ ...errors, fournisseur: null });
    };

    const handleAjouterProduit = (produit) => {
        setProduitSelectionne(produit);
        setPrix(produit.prix_achat_ht || 0);
        setQuantite(1);
        setRemise(0);
        setShowProduitModal(true);
        setShowProduitResults(false);
    };

    const handleConfirmerProduit = () => {
        const nouvelleLigne = {
            id_produit: produitSelectionne.id_produit,
            reference: produitSelectionne.reference,
            designation: produitSelectionne.designation,
            quantite_commandee: quantite,
            prix_unitaire_ht: prix,
            remise: remise,
            tva_taux: produitSelectionne.tva_taux || 0.00,
            css_taux: produitSelectionne.css_taux || 0.00,
            montant_ht: quantite * prix * (1 - remise / 100)
        };
        setFormData({ ...formData, lignes: [...formData.lignes, nouvelleLigne] });
        setShowProduitModal(false);
        setSearchProduit('');
        showToast('success', 'Produit ajouté', `${produitSelectionne.designation} ajouté à la commande`);
    };

    const handleSupprimerLigne = (index) => {
        const nouvellesLignes = formData.lignes.filter((_, i) => i !== index);
        setFormData({ ...formData, lignes: nouvellesLignes });
    };

    const calculerTotaux = () => {
        const montantHT = formData.lignes.reduce((acc, l) => acc + (l.montant_ht || 0), 0);
        const tva = formData.lignes.reduce((acc, l) => acc + ((l.montant_ht || 0) * (l.tva_taux || 0) / 100), 0);
        const css = formData.lignes.reduce((acc, l) => acc + ((l.montant_ht || 0) * (l.css_taux || 0) / 100), 0);
        return { montant_ht: montantHT, montant_tva: tva, montant_css: css, montant_ttc: montantHT + tva + css };
    };

    const validateForm = () => {
        const newErrors = {};
        if (!formData.id_fournisseur) newErrors.fournisseur = "Veuillez sélectionner un fournisseur";
        if (formData.lignes.length === 0) newErrors.lignes = "Ajoutez au moins un produit";
        return newErrors;
    };

    const resetForm = () => {
        setFormData({
            id_fournisseur: '',
            fournisseur_nom: '',
            date_commande: new Date().toISOString().split('T')[0],
            date_livraison_prevue: '',
            conditions_reglement: '',
            lignes: []
        });
        setSearchFournisseur('');
        setSearchProduit('');
        setErrors({});
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const newErrors = validateForm();
        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            showToast('error', 'Champs requis', 'Veuillez compléter le formulaire');
            return;
        }

        setLoading(true);
        try {
            const token = localStorage.getItem("token");
            const userStr = localStorage.getItem("user");
            const user = userStr ? JSON.parse(userStr) : null;
            const totaux = calculerTotaux();

            const response = await fetch(`${API_BASE_URL}/commandes_fournisseurs.php`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    action: 'create',
                    id_fournisseur: formData.id_fournisseur,
                    date_commande: formData.date_commande,
                    date_livraison_prevue: formData.date_livraison_prevue,
                    conditions_reglement: formData.conditions_reglement,
                    ...totaux,
                    lignes: formData.lignes,
                    id_utilisateur: user?.id_utilisateur || 1
                })
            });
            const result = await response.json();

            if (result.success) {
                showToast('success', 'Commande créée', 'Enregistrement réussi ! Redirection...');
                setTimeout(() => navigate('/achats/commandes'), 1500);
            } else {
                showToast('error', 'Erreur', result.message || "Erreur lors de la création");
            }
        } catch (err) {
            showToast('error', 'Erreur de connexion', "Impossible de contacter le serveur");
        } finally {
            setLoading(false);
        }
    };

    const totaux = calculerTotaux();
    const fournisseursFiltres = fournisseurs.filter(f =>
        f.raison_sociale?.toLowerCase().includes(searchFournisseur.toLowerCase()) ||
        f.code_fournisseur?.toLowerCase().includes(searchFournisseur.toLowerCase())
    );
    const produitsFiltres = produits.filter(p =>
        p.designation?.toLowerCase().includes(searchProduit.toLowerCase()) ||
        p.reference?.toLowerCase().includes(searchProduit.toLowerCase())
    );

    /* ============================================================
       RENDU
       ============================================================ */
    return (
        <>
            <Header />
            <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                <div className="container-fluid">

                    <ToastNotification
                        show={toast.show}
                        onClose={hideToast}
                        type={toast.type}
                        title={toast.title}
                        message={toast.message}
                    />

                    {/* ===== EN-TÊTE MODERNE ===== */}
                    <div className="header-modern mb-4">
                        <div className="header-left">
                            <div className="header-icon-wrapper">
                                <i className="ri-shopping-cart-2-line"></i>
                            </div>
                            <div>
                                <h5 className="header-title">Nouvelle commande</h5>
                                <div className="d-flex flex-wrap gap-2 mt-1">
                                    <span className="badge-modern">
                                        <i className="ri-add-circle-line"></i>
                                        Créer une commande fournisseur
                                    </span>
                                    {formData.lignes.length > 0 && (
                                        <span className="badge-modern" style={{ background: `${COLORS.success}15`, color: COLORS.success }}>
                                            <i className="ri-shopping-bag-line"></i>
                                            {formData.lignes.length} produit(s)
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="d-flex gap-2 flex-wrap">
                            <Button className="btn-modern-outline" onClick={() => navigate('/achats/commandes')}>
                                <i className="ri-arrow-left-line"></i> Retour
                            </Button>
                            <Button className="btn-modern-outline" onClick={() => navigate('/achats/fournisseurs')}>
                                <i className="ri-truck-line"></i> Fournisseurs
                            </Button>
                        </div>
                    </div>

                    <Form onSubmit={handleSubmit}>
                        <Row className="g-4">
                            <Col lg={8}>

                                {/* ===== SÉLECTION FOURNISSEUR ===== */}
                                <Card className="border-0 shadow-sm rounded-3 mb-4">
                                    <Card.Header className="card-header-gradient">
                                        <h5 className="mb-0 text-white d-flex align-items-center">
                                            <i className="ri-truck-line me-2"></i>
                                            Fournisseur
                                        </h5>
                                    </Card.Header>
                                    <Card.Body className="p-4">
                                        <Form.Group>
                                            <Form.Label className="form-label-modern">
                                                Rechercher un fournisseur <span className="text-danger">*</span>
                                            </Form.Label>
                                            <InputGroup className="input-modern">
                                                <InputGroup.Text><i className="ri-search-line"></i></InputGroup.Text>
                                                <Form.Control
                                                    ref={fournisseurInputRef}
                                                    type="text"
                                                    placeholder="Nom du fournisseur..."
                                                    value={searchFournisseur}
                                                    onChange={(e) => {
                                                        setSearchFournisseur(e.target.value);
                                                        setShowFournisseurResults(true);
                                                        setTimeout(updateFournisseurPosition, 10);
                                                    }}
                                                    onFocus={() => {
                                                        setShowFournisseurResults(true);
                                                        updateFournisseurPosition();
                                                    }}
                                                    onBlur={() => setTimeout(() => setShowFournisseurResults(false), 200)}
                                                    isInvalid={!!errors.fournisseur}
                                                />
                                                <Form.Control.Feedback type="invalid">
                                                    {errors.fournisseur}
                                                </Form.Control.Feedback>
                                            </InputGroup>

                                            {showFournisseurResults && searchFournisseur && fournisseursFiltres.length > 0 && ReactDOM.createPortal(
                                                <div
                                                    className="dropdown-results-charte"
                                                    style={{
                                                        position: 'absolute',
                                                        top: fournisseurPosition.top,
                                                        left: fournisseurPosition.left,
                                                        width: fournisseurPosition.width,
                                                        zIndex: 99999
                                                    }}
                                                >
                                                    {fournisseursFiltres.map(f => (
                                                        <div
                                                            key={f.id_fournisseur}
                                                            className="dropdown-item-charte"
                                                            onClick={() => handleFournisseurSelect(f)}
                                                        >
                                                            <strong className="d-block">{f.raison_sociale}</strong>
                                                            <small className="text-muted">
                                                                {f.code_fournisseur} | {f.telephone || 'Tél non renseigné'}
                                                            </small>
                                                        </div>
                                                    ))}
                                                </div>,
                                                document.body
                                            )}
                                        </Form.Group>

                                        {formData.id_fournisseur && (
                                            <div className="selected-card-charte mt-3">
                                                <div className="d-flex justify-content-between align-items-center">
                                                    <div>
                                                        <div className="d-flex align-items-center gap-2">
                                                            <i className="ri-checkbox-circle-fill" style={{ color: COLORS.success, fontSize: '1.2rem' }}></i>
                                                            <span className="fw-semibold">{formData.fournisseur_nom}</span>
                                                            <span className="badge-selected-charte">
                                                                <i className="ri-check-line"></i> Sélectionné
                                                            </span>
                                                        </div>
                                                        {formData.conditions_reglement && (
                                                            <small className="text-muted d-block mt-1">
                                                                <i className="ri-file-text-line me-1"></i>
                                                                {formData.conditions_reglement}
                                                            </small>
                                                        )}
                                                    </div>
                                                    <Button
                                                        variant="link"
                                                        className="text-muted p-0 btn-remove-charte"
                                                        onClick={() => {
                                                            setFormData({
                                                                ...formData,
                                                                id_fournisseur: '',
                                                                fournisseur_nom: '',
                                                                conditions_reglement: ''
                                                            });
                                                            setSearchFournisseur('');
                                                        }}
                                                    >
                                                        <i className="ri-close-circle-line fs-5"></i>
                                                    </Button>
                                                </div>
                                            </div>
                                        )}
                                    </Card.Body>
                                </Card>

                                {/* ===== DATES ===== */}
                                <Card className="border-0 shadow-sm rounded-3 mb-4">
                                    <Card.Header className="card-header-gradient">
                                        <h5 className="mb-0 text-white d-flex align-items-center">
                                            <i className="ri-calendar-line me-2"></i>
                                            Dates
                                        </h5>
                                    </Card.Header>
                                    <Card.Body className="p-4">
                                        <Row className="g-3">
                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label className="form-label-modern">Date commande</Form.Label>
                                                    <InputGroup className="input-modern">
                                                        <InputGroup.Text><i className="ri-calendar-line"></i></InputGroup.Text>
                                                        <Form.Control
                                                            type="date"
                                                            value={formData.date_commande}
                                                            onChange={(e) => setFormData({ ...formData, date_commande: e.target.value })}
                                                        />
                                                    </InputGroup>
                                                </Form.Group>
                                            </Col>
                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label className="form-label-modern">Date livraison prévue</Form.Label>
                                                    <InputGroup className="input-modern">
                                                        <InputGroup.Text><i className="ri-truck-line"></i></InputGroup.Text>
                                                        <Form.Control
                                                            type="date"
                                                            value={formData.date_livraison_prevue}
                                                            onChange={(e) => setFormData({ ...formData, date_livraison_prevue: e.target.value })}
                                                        />
                                                    </InputGroup>
                                                </Form.Group>
                                            </Col>
                                        </Row>
                                    </Card.Body>
                                </Card>

                                {/* ===== PRODUITS ===== */}
                                <Card className="border-0 shadow-sm rounded-3">
                                    <Card.Header className="card-header-gradient d-flex justify-content-between align-items-center">
                                        <h5 className="mb-0 text-white d-flex align-items-center">
                                            <i className="ri-shopping-bag-3-line me-2"></i>
                                            Produits
                                        </h5>
                                        <Button
                                            className="btn-modern-outline-light"
                                            size="sm"
                                            onClick={() => setShowProduitResults(!showProduitResults)}
                                        >
                                            <i className="ri-add-line me-1"></i>
                                            Ajouter un produit
                                        </Button>
                                    </Card.Header>
                                    <Card.Body className="p-4">
                                        {showProduitResults && (
                                            <div className="mb-4">
                                                <InputGroup className="input-modern">
                                                    <InputGroup.Text><i className="ri-search-line"></i></InputGroup.Text>
                                                    <Form.Control
                                                        ref={produitInputRef}
                                                        type="text"
                                                        placeholder="Rechercher un produit..."
                                                        value={searchProduit}
                                                        onChange={(e) => {
                                                            setSearchProduit(e.target.value);
                                                            setTimeout(updateProduitPosition, 10);
                                                        }}
                                                        onFocus={updateProduitPosition}
                                                    />
                                                </InputGroup>

                                                {searchProduit && produitsFiltres.length > 0 && ReactDOM.createPortal(
                                                    <div
                                                        className="dropdown-results-charte"
                                                        style={{
                                                            position: 'absolute',
                                                            top: produitPosition.top,
                                                            left: produitPosition.left,
                                                            width: produitPosition.width,
                                                            zIndex: 99999
                                                        }}
                                                    >
                                                        {produitsFiltres.map(p => (
                                                            <div
                                                                key={p.id_produit}
                                                                className="dropdown-item-charte d-flex justify-content-between align-items-center"
                                                                onClick={() => handleAjouterProduit(p)}
                                                            >
                                                                <div>
                                                                    <strong className="d-block">{p.designation}</strong>
                                                                    <small className="text-muted">
                                                                        Réf: {p.reference} | Prix: {formatCurrency(p.prix_achat_ht)}
                                                                    </small>
                                                                </div>
                                                                <span className="badge-selected-charte">
                                                                    <i className="ri-add-line"></i> Ajouter
                                                                </span>
                                                            </div>
                                                        ))}
                                                    </div>,
                                                    document.body
                                                )}
                                            </div>
                                        )}

                                        {formData.lignes.length > 0 ? (
                                            <div className="table-responsive">
                                                <Table className="align-middle mb-0 table-charte">
                                                    <thead>
                                                        <tr>
                                                            <th>Produit</th>
                                                            <th>Réf.</th>
                                                            <th className="text-center">Qté</th>
                                                            <th className="text-end">Prix unit.</th>
                                                            <th className="text-center">Remise</th>
                                                            <th className="text-end">Total HT</th>
                                                            <th className="text-center"></th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {formData.lignes.map((ligne, index) => (
                                                            <tr key={index}>
                                                                <td className="fw-semibold">{ligne.designation}</td>
                                                                <td>
                                                                    <span className="reference-badge-charte">{ligne.reference}</span>
                                                                </td>
                                                                <td className="text-center">
                                                                    <span className="quantity-badge-charte">{ligne.quantite_commandee}</span>
                                                                </td>
                                                                <td className="text-end">{formatCurrency(ligne.prix_unitaire_ht)}</td>
                                                                <td className="text-center">{ligne.remise}%</td>
                                                                <td className="text-end fw-bold" style={{ color: COLORS.primary }}>
                                                                    {formatCurrency(ligne.montant_ht)}
                                                                </td>
                                                                <td className="text-center">
                                                                    <button
                                                                        className="action-btn-danger-charte"
                                                                        onClick={() => handleSupprimerLigne(index)}
                                                                    >
                                                                        <i className="ri-delete-bin-line"></i>
                                                                    </button>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </Table>
                                            </div>
                                        ) : (
                                            <div className="empty-state-charte">
                                                <div className="empty-state-icon-charte">
                                                    <i className="ri-shopping-cart-line"></i>
                                                </div>
                                                <h6 className="fw-semibold mb-1">Aucun produit ajouté</h6>
                                                <p className="text-muted small mb-0">
                                                    Cliquez sur "Ajouter un produit" pour commencer
                                                </p>
                                            </div>
                                        )}
                                    </Card.Body>
                                </Card>
                            </Col>

                            <Col lg={4}>
                                {/* ===== RÉSUMÉ ===== */}
                                <Card className="border-0 shadow-sm rounded-3 sticky-top" style={{ top: '20px' }}>
                                    <Card.Header className="card-header-gradient">
                                        <h5 className="mb-0 text-white d-flex align-items-center">
                                            <i className="ri-calculator-line me-2"></i>
                                            Résumé
                                        </h5>
                                    </Card.Header>
                                    <Card.Body className="p-4">
                                        <div className="recap-line-charte d-flex justify-content-between mb-2">
                                            <span className="text-muted">Total HT</span>
                                            <span className="fw-bold">{formatCurrency(totaux.montant_ht)}</span>
                                        </div>
                                        <div className="recap-line-charte d-flex justify-content-between mb-2">
                                            <span className="text-muted">TVA</span>
                                            <span className="fw-semibold" style={{ color: COLORS.warning }}>
                                                {formatCurrency(totaux.montant_tva)}
                                            </span>
                                        </div>
                                        <div className="recap-line-charte d-flex justify-content-between mb-3">
                                            <span className="text-muted">CSS</span>
                                            <span className="fw-semibold" style={{ color: COLORS.info }}>
                                                {formatCurrency(totaux.montant_css)}
                                            </span>
                                        </div>
                                        <hr />
                                        <div className="recap-line-charte d-flex justify-content-between mb-4">
                                            <span className="fw-bold">Total TTC</span>
                                            <span className="fw-bold fs-4" style={{ color: COLORS.primary }}>
                                                {formatCurrency(totaux.montant_ttc)}
                                            </span>
                                        </div>

                                        <Form.Group className="mb-4">
                                            <Form.Label className="form-label-modern">Conditions de règlement</Form.Label>
                                            <InputGroup className="input-modern">
                                                <InputGroup.Text><i className="ri-file-text-line"></i></InputGroup.Text>
                                                <Form.Control
                                                    as="textarea"
                                                    rows={2}
                                                    value={formData.conditions_reglement}
                                                    onChange={(e) => setFormData({ ...formData, conditions_reglement: e.target.value })}
                                                    placeholder="Ex: Paiement 30 jours..."
                                                />
                                            </InputGroup>
                                        </Form.Group>

                                        <div className="d-grid gap-2">
                                            <Button
                                                type="submit"
                                                className="btn-modern-primary justify-content-center"
                                                disabled={loading}
                                            >
                                                {loading ? (
                                                    <><Spinner size="sm" className="me-2" />Création...</>
                                                ) : (
                                                    <><i className="ri-check-line me-2"></i>Créer la commande</>
                                                )}
                                            </Button>
                                            <Button
                                                type="button"
                                                className="btn-modern-outline justify-content-center"
                                                onClick={resetForm}
                                            >
                                                <i className="ri-refresh-line me-1"></i> Réinitialiser
                                            </Button>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>
                    </Form>

                    {/* ===== MODAL AJOUT PRODUIT ===== */}
                    <Modal show={showProduitModal} onHide={() => setShowProduitModal(false)} centered className="modal-charte">
                        <Modal.Header closeButton className="modal-header-charte" style={{ background: `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.primaryDark})` }}>
                            <div className="d-flex align-items-center gap-3">
                                <div className="modal-header-icon"><i className="ri-shopping-cart-line"></i></div>
                                <div>
                                    <Modal.Title className="fs-5 fw-bold text-white mb-0">Ajouter un produit</Modal.Title>
                                    <p className="text-white-50 mb-0 small">{produitSelectionne?.designation}</p>
                                </div>
                            </div>
                        </Modal.Header>
                        <Modal.Body className="p-4">
                            <Form>
                                <Form.Group className="mb-3">
                                    <Form.Label className="form-label-modern">Quantité</Form.Label>
                                    <InputGroup className="input-modern">
                                        <InputGroup.Text><i className="ri-hashtag"></i></InputGroup.Text>
                                        <Form.Control
                                            type="number"
                                            min="1"
                                            value={quantite}
                                            onChange={(e) => setQuantite(parseInt(e.target.value) || 1)}
                                        />
                                    </InputGroup>
                                </Form.Group>
                                <Form.Group className="mb-3">
                                    <Form.Label className="form-label-modern">Prix unitaire HT</Form.Label>
                                    <InputGroup className="input-modern">
                                        <InputGroup.Text><i className="ri-money-dollar-circle-line"></i></InputGroup.Text>
                                        <Form.Control
                                            type="number"
                                            min="0"
                                            step="0.01"
                                            value={prix}
                                            onChange={(e) => setPrix(parseFloat(e.target.value) || 0)}
                                        />
                                        <InputGroup.Text className="bg-light">FCFA</InputGroup.Text>
                                    </InputGroup>
                                </Form.Group>
                                <Form.Group className="mb-3">
                                    <Form.Label className="form-label-modern">Remise (%)</Form.Label>
                                    <InputGroup className="input-modern">
                                        <InputGroup.Text><i className="ri-percent-line"></i></InputGroup.Text>
                                        <Form.Control
                                            type="number"
                                            min="0"
                                            max="100"
                                            value={remise}
                                            onChange={(e) => setRemise(parseFloat(e.target.value) || 0)}
                                        />
                                    </InputGroup>
                                </Form.Group>
                                <hr />
                                <div className="d-flex justify-content-between recap-line-charte">
                                    <span className="fw-semibold">Total HT</span>
                                    <strong className="fs-5" style={{ color: COLORS.primary }}>
                                        {formatCurrency(quantite * prix * (1 - remise / 100))}
                                    </strong>
                                </div>
                            </Form>
                        </Modal.Body>
                        <Modal.Footer className="border-0 pb-4 px-4 pt-0">
                            <Button className="btn-modern-outline" onClick={() => setShowProduitModal(false)}>
                                Annuler
                            </Button>
                            <Button className="btn-modern-primary" onClick={handleConfirmerProduit}>
                                <i className="ri-add-line me-2"></i>Ajouter
                            </Button>
                        </Modal.Footer>
                    </Modal>
                </div>
                <Footer />
            </div>

            {/* ===== STYLES CHARTE ===== */}
            <style>{`
                :root {
                    --primary: ${COLORS.primary || '#295AA7'};
                    --primary-dark: ${COLORS.primaryDark || '#1e3f7a'};
                    --secondary: ${COLORS.secondary || '#21ACCD'};
                    --success: ${COLORS.success || '#10b981'};
                    --danger: ${COLORS.danger || '#ef4444'};
                    --warning: ${COLORS.warning || '#f59e0b'};
                    --info: ${COLORS.info || '#3b82f6'};
                }

                /* ===== EN-TÊTE ===== */
                .header-modern {
                    background: white; padding: 1.2rem 1.8rem;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.04);
                    border-left: 4px solid var(--secondary);
                    border-radius: 12px;
                    display: flex; align-items: center; justify-content: space-between;
                    flex-wrap: wrap; gap: 1rem;
                }
                .header-left { display: flex; align-items: center; gap: 1rem; }
                .header-icon-wrapper {
                    width: 52px; height: 52px;
                    background: linear-gradient(135deg, var(--primary), var(--secondary));
                    display: flex; align-items: center; justify-content: center;
                    border-radius: 14px; color: white; font-size: 1.5rem;
                    box-shadow: 0 4px 12px rgba(41, 90, 167, 0.25);
                }
                .header-title { color: #1e293b; font-size: 1.3rem; font-weight: 700; margin: 0; }
                .badge-modern {
                    background: #f1f4f9; color: #475569;
                    padding: 0.3rem 0.8rem; border-radius: 50px;
                    font-size: 0.75rem; font-weight: 600;
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .badge-modern i { color: var(--secondary); }
                .card-header-gradient {
                    background: linear-gradient(135deg, var(--primary) 0%, var(--primary-dark) 100%);
                    border-top-left-radius: 12px !important;
                    border-top-right-radius: 12px !important;
                    padding: 1rem 1.5rem;
                    border: none;
                }

                /* ===== FORMULAIRES ===== */
                .form-label-modern {
                    font-weight: 600; color: #334155;
                    font-size: 0.85rem; margin-bottom: 0.4rem;
                }
                .input-modern .input-group-text {
                    background: #f8fafc; border: 2px solid #e2e8f0;
                    border-right: none; color: var(--primary);
                    border-radius: 10px 0 0 10px;
                }
                .input-modern .form-control,
                .input-modern .form-select {
                    border: 2px solid #e2e8f0; border-left: none;
                    border-radius: 0 10px 10px 0;
                    padding: 0.6rem 0.9rem; font-size: 0.9rem;
                    transition: all 0.2s ease;
                }
                .input-modern .form-control:focus,
                .input-modern .form-select:focus {
                    border-color: var(--secondary);
                    box-shadow: 0 0 0 3px rgba(33,172,205,0.1);
                }
                .input-modern .input-group-text.bg-light {
                    background: #f1f5f9 !important;
                    border-left: none;
                    border-radius: 0 10px 10px 0;
                    color: #64748b; font-weight: 600; font-size: 0.85rem;
                }

                /* ===== DROPDOWN ===== */
                .dropdown-results-charte {
                    background: white;
                    border: 1px solid #e2e8f0;
                    border-radius: 12px;
                    box-shadow: 0 20px 40px rgba(0,0,0,0.15);
                    max-height: 300px;
                    overflow-y: auto;
                    animation: fadeIn 0.2s ease-out;
                }
                .dropdown-item-charte {
                    padding: 0.85rem 1rem;
                    border-bottom: 1px solid #f1f5f9;
                    cursor: pointer;
                    transition: all 0.15s ease;
                }
                .dropdown-item-charte:last-child { border-bottom: none; }
                .dropdown-item-charte:hover { background: #f8fafc; }

                /* ===== SELECTED CARD ===== */
                .selected-card-charte {
                    background: linear-gradient(135deg, #ecfdf5, #d1fae5);
                    border-left: 3px solid var(--success);
                    border-radius: 12px;
                    padding: 1rem 1.25rem;
                    transition: all 0.2s ease;
                }
                .selected-card-charte:hover { transform: translateX(3px); }
                .badge-selected-charte {
                    display: inline-flex; align-items: center; gap: 4px;
                    background: linear-gradient(135deg, var(--success), #059669);
                    color: white;
                    padding: 0.2rem 0.65rem;
                    border-radius: 50px;
                    font-size: 0.7rem; font-weight: 600;
                }

                /* ===== BOUTON LIGHT (header card) ===== */
                .btn-modern-outline-light {
                    background: rgba(255,255,255,0.15);
                    border: 1.5px solid rgba(255,255,255,0.3);
                    color: white;
                    padding: 0.4rem 1rem;
                    border-radius: 50px;
                    font-size: 0.8rem; font-weight: 500;
                    transition: all 0.2s ease;
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .btn-modern-outline-light:hover {
                    background: rgba(255,255,255,0.25);
                    border-color: rgba(255,255,255,0.5);
                    color: white;
                    transform: translateY(-1px);
                }

                /* ===== TABLE ===== */
                .table-charte thead th {
                    background: #f8fafc; color: #475569;
                    font-weight: 600; font-size: 0.7rem;
                    text-transform: uppercase; letter-spacing: 0.5px;
                    border-bottom: 2px solid #e2e8f0;
                    padding: 0.9rem 0.75rem;
                }
                .table-charte tbody td {
                    padding: 0.9rem 0.75rem;
                    vertical-align: middle;
                    border-bottom: 1px solid #f1f5f9;
                    font-size: 0.85rem;
                }
                .table-charte tbody tr { transition: background 0.15s ease; }
                .table-charte tbody tr:hover { background: #f8fafc; }
                .reference-badge-charte {
                    display: inline-flex; align-items: center; gap: 4px;
                    background: #f1f5f9; padding: 0.25rem 0.6rem;
                    border-radius: 6px; font-size: 0.7rem;
                    font-family: monospace; color: #475569;
                }
                .quantity-badge-charte {
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    min-width: 50px;
                    padding: 0.25rem 0.5rem;
                    background: #f1f5f9;
                    border-radius: 12px;
                    font-weight: 600;
                    font-size: 0.85rem;
                    color: #1e293b;
                }
                .action-btn-danger-charte {
                    width: 36px; height: 36px; border-radius: 10px;
                    display: inline-flex; align-items: center; justify-content: center;
                    background: white; border: 1.5px solid #e2e8f0;
                    color: #64748b; font-size: 1rem;
                    transition: all 0.2s ease;
                    cursor: pointer;
                }
                .action-btn-danger-charte:hover {
                    border-color: var(--danger); color: var(--danger);
                    background: #fef2f2;
                    transform: translateY(-2px);
                    box-shadow: 0 6px 16px rgba(239,68,68,0.15);
                }
                .btn-remove-charte:hover { color: var(--danger) !important; }

                /* ===== EMPTY STATE ===== */
                .empty-state-charte { text-align: center; padding: 3rem 2rem; }
                .empty-state-icon-charte {
                    width: 72px; height: 72px;
                    background: linear-gradient(135deg, #f1f5f9, #e2e8f0);
                    border-radius: 50%;
                    display: flex; align-items: center; justify-content: center;
                    margin: 0 auto 1rem; font-size: 1.8rem; color: #94a3b8;
                }

                /* ===== RÉCAP ===== */
                .recap-line-charte { padding: 0.5rem 0; }

                /* ===== BOUTONS ===== */
                .btn-modern-outline {
                    border: 1.5px solid #d1d5db; background: white;
                    padding: 0.5rem 1.4rem; border-radius: 50px;
                    font-size: 0.85rem; font-weight: 500; color: #4b5563;
                    transition: all 0.2s ease;
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .btn-modern-outline:hover {
                    border-color: var(--secondary); color: var(--secondary);
                    background: #f0fbff; transform: translateY(-1px);
                    box-shadow: 0 4px 8px rgba(33,172,205,0.15);
                }
                .btn-modern-primary {
                    background: linear-gradient(135deg, var(--primary), var(--primary-dark));
                    border: none; color: white; padding: 0.5rem 1.6rem;
                    border-radius: 50px; font-weight: 600; font-size: 0.85rem;
                    transition: all 0.2s ease;
                    box-shadow: 0 4px 12px rgba(41,90,167,0.3);
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .btn-modern-primary:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 6px 18px rgba(41,90,167,0.4); color: white;
                }
                .btn-modern-primary:disabled { opacity: 0.6; transform: none; }

                /* ===== MODAL ===== */
                .modal-charte .modal-content {
                    border: none; border-radius: 20px; overflow: hidden;
                    box-shadow: 0 30px 80px rgba(0,0,0,0.15);
                }
                .modal-header-charte { padding: 1.25rem 1.5rem; border: none; }
                .modal-header-charte .btn-close { filter: brightness(0) invert(1); opacity: 0.8; }
                .modal-header-icon {
                    width: 48px; height: 48px; border-radius: 12px;
                    background: rgba(255,255,255,0.2);
                    display: flex; align-items: center; justify-content: center;
                    font-size: 1.5rem; color: white;
                }

                /* ===== TOAST ===== */
                .toast-notification-fixed {
                    position: fixed; bottom: 24px; right: 24px;
                    z-index: 9999; animation: slideInRight 0.3s ease-out;
                }
                .toast-modern {
                    background: white; border-radius: 16px;
                    box-shadow: 0 20px 40px rgba(0,0,0,0.15);
                    min-width: 320px; max-width: 400px; overflow: hidden;
                }
                .toast-header-custom {
                    padding: 12px 20px;
                    display: flex; align-items: center; justify-content: space-between;
                }
                .toast-close-btn {
                    background: transparent; border: none;
                    color: white; cursor: pointer; opacity: 0.8;
                }
                .toast-close-btn:hover { opacity: 1; }
                .toast-body-custom { padding: 16px 20px; }
                .toast-body-custom p { color: #1e293b; font-size: 0.9rem; }
                .toast-progress { height: 3px; width: 100%; animation: shrink linear forwards; }

                /* ===== ANIMATIONS ===== */
                @keyframes fadeIn {
                    from { opacity: 0; transform: translateY(-5px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                @keyframes slideInRight {
                    from { opacity: 0; transform: translateX(100%); }
                    to { opacity: 1; transform: translateX(0); }
                }
                @keyframes shrink {
                    from { width: 100%; }
                    to { width: 0%; }
                }

                /* ===== RESPONSIVE ===== */
                @media (max-width: 768px) {
                    .header-modern { padding: 1rem; }
                    .header-icon-wrapper { width: 44px; height: 44px; font-size: 1.2rem; }
                    .header-title { font-size: 1.1rem; }
                    .toast-notification-fixed { bottom: 12px; right: 12px; left: 12px; }
                    .toast-modern { min-width: auto; max-width: none; }
                }
                .rounded-3 { border-radius: 12px !important; }
            `}</style>
        </>
    );
}